import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Notice, Progress } from "../components/Notice";
import { Player } from "../components/Player";
import { api, ApiError, type LibraryItem } from "../lib/api";

export function Library() {
  const [items, setItems] = useState<LibraryItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; title: string } | null>(null);

  useEffect(() => {
    api
      .listLibrary()
      .then(setItems)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Could not load your library."));
  }, []);

  async function remove(item: LibraryItem) {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await api.remove(item.id);
      setItems((prev) => prev?.filter((i) => i.id !== item.id) ?? null);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : "Could not delete.");
    }
  }

  async function rename() {
    if (!renaming) return;
    try {
      const saved = await api.save(renaming.id, renaming.title.trim() || "Untitled sound");
      setItems((prev) => prev?.map((i) => (i.id === saved.id ? saved : i)) ?? null);
      setRenaming(null);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : "Could not rename.");
    }
  }

  return (
    <section className="stack">
      <div>
        <h1>My Library</h1>
        <p className="lede">Every sound you saved, private to your SoundLayer ID.</p>
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {items === null && !error && <Progress label="Loading your library…" />}
      {items && items.length === 0 && (
        <div className="empty reveal">
          <h3>Nothing saved yet</h3>
          <p>Generate a sound and save it. It will show up here.</p>
          <Link className="btn btn-primary" to="/">
            Create a sound
          </Link>
        </div>
      )}
      {items && items.length > 0 && (
        <ul className="list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {items.map((item) => (
            <li key={item.id} className="item reveal">
              <div>
                {renaming?.id === item.id ? (
                  <form
                    className="rename"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void rename();
                    }}
                  >
                    <input
                      className="input"
                      aria-label="New name"
                      value={renaming.title}
                      maxLength={80}
                      autoFocus
                      onChange={(e) => setRenaming({ id: item.id, title: e.target.value })}
                    />
                    <button className="btn btn-primary" type="submit">
                      Save
                    </button>
                    <button className="btn btn-quiet" type="button" onClick={() => setRenaming(null)}>
                      Cancel
                    </button>
                  </form>
                ) : (
                  <h3>{item.title}</h3>
                )}
                <div className="meta">
                  {item.duration_seconds}s · {item.loop ? "loops" : "plays once"}
                  {item.saved_at && ` · saved ${new Date(item.saved_at).toLocaleDateString()}`}
                </div>
              </div>
              <div className="actions">
                <button
                  className="btn"
                  aria-expanded={openId === item.id}
                  onClick={() => setOpenId(openId === item.id ? null : item.id)}
                >
                  {openId === item.id ? "Hide" : "Play"}
                </button>
                <button className="btn btn-quiet" onClick={() => setRenaming({ id: item.id, title: item.title })}>
                  Rename
                </button>
                <button className="btn btn-quiet btn-danger" onClick={() => remove(item)} aria-label={`Delete ${item.title}`}>
                  Delete
                </button>
              </div>
              <p className="prompt">{item.prompt}</p>
              {openId === item.id && <Player itemId={item.id} loop={item.loop} />}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
